import { useState, useMemo, useCallback, useEffect } from 'react';
import { getCurrentUserPermission, getFeatureCode } from '../helpers/utils.js';
import { useAppConfig } from './useAppConfig.js';
import { useFeatures } from './useFeatures.js';
import { useToken } from './useToken.js';
import { useCurrentUser } from './useCurrentUser.js';
import { useCachedPermissions } from './useCachedPermissions.js';
var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, {
  enumerable: true,
  configurable: true,
  writable: true,
  value
}) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {})) if (__hasOwnProp.call(b, prop)) __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols) for (var prop of __getOwnPropSymbols(b)) {
    if (__propIsEnum.call(b, prop)) __defNormalProp(a, prop, b[prop]);
  }
  return a;
};
const usePermission = (feature, createdBy, disable, permissionType) => {
  const [, setError] = useState(null);
  const features = useFeatures();
  const currentFeature = useMemo(() => getCurrentFeature(feature, features), [feature, features]);
  const config = useAppConfig();
  const {
    fetchToken
  } = useToken();
  const {
    user: currentUser
  } = useCurrentUser();
  const {
    cachedPermissions,
    setCachedPermissions
  } = useCachedPermissions();
  const [userPermission, setUserPermission] = useState(void 0);
  const [isLoading, setIsLoading] = useState(true);
  const getPermission = useCallback(async (feature2, permissionType2) => {
    const permissionKey = getCachedPermissionKey(createdBy, feature2, permissionType2);
    if (cachedPermissions[permissionKey]) {
      const permission = cachedPermissions[permissionKey];
      setUserPermission(permission);
      setIsLoading(false);
      return permission;
    } else {
      if (feature2) {
        try {
          const token = await fetchToken();
          const response = await getCurrentUserPermission(token, config, feature2, permissionType2);
          if (response) {
            let ownRecord = response.create && isOwnRecord(createdBy, currentUser);
            let updatedPermissions = __spreadValues({
              [permissionKey]: {
                canCreate: response.create,
                canDelete: response.delete || ownRecord,
                canModify: response.update || ownRecord,
                canView: response.read || ownRecord || response.create && !createdBy,
                featurePermission: {
                  viewAll: response.read,
                  create: response.create,
                  modifyAll: response.update,
                  deleteAll: response.delete
                }
              }
            }, cachedPermissions);
            setCachedPermissions(updatedPermissions);
            const permission = updatedPermissions[permissionKey];
            setUserPermission(permission);
            return permission;
          }
        } catch (error) {
          setError(() => {
            throw error;
          });
        } finally {
          setIsLoading(false);
        }
      } else {
        const permission = {
          canCreate: false,
          canDelete: false,
          canModify: false,
          canView: false,
          featurePermission: {
            create: false,
            modifyAll: false,
            deleteAll: false,
            viewAll: false
          }
        };
        setUserPermission(permission);
        setIsLoading(false);
        return permission;
      }
    }
  },
  // Removed Permissions dependency to avoid multiple re rendering, which will corrupt the permission data in context.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [createdBy, currentUser.id, setCachedPermissions]);
  useEffect(() => {
    if (!disable) {
      getPermission(currentFeature, {
        brn: permissionType == null ? void 0 : permissionType.brn,
        ban: permissionType == null ? void 0 : permissionType.ban,
        product: permissionType == null ? void 0 : permissionType.product,
        serviceNumber: permissionType == null ? void 0 : permissionType.serviceNumber
      });
    }
  }, [getPermission, disable, permissionType == null ? void 0 : permissionType.brn, permissionType == null ? void 0 : permissionType.ban, permissionType == null ? void 0 : permissionType.product, permissionType == null ? void 0 : permissionType.serviceNumber, currentFeature]);
  return {
    userPermission,
    isLoading,
    getPermission
  };
};
const getCachedPermissionKey = (createdBy, feature, permissionType) => {
  const featureCode = getFeatureCode(feature);
  return featureCode + "|" + (createdBy ? createdBy : "") + "|" + ((permissionType == null ? void 0 : permissionType.brn) ? permissionType.brn : "") + "|" + ((permissionType == null ? void 0 : permissionType.ban) ? permissionType.ban : "") + "|" + ((permissionType == null ? void 0 : permissionType.serviceNumber) ? permissionType.serviceNumber : "") + "|" + ((permissionType == null ? void 0 : permissionType.product) ? permissionType.product : "");
};
const isOwnRecord = (createdBy, currentUser) => {
  if (createdBy) {
    return typeof createdBy === "number" && createdBy === currentUser.id || typeof createdBy === "string" && createdBy === currentUser.emailId;
  }
  return false;
};
const getCurrentFeature = (feature, features) => {
  var _a;
  const dataKey = typeof feature === "string" || (feature == null ? void 0 : feature.featureCode) ? "featureCode" : "featureName";
  const featureCode = getFeatureCode(feature);
  const appModule = features.find(module => module.features.some(feature2 => feature2[dataKey] === featureCode));
  return ((_a = appModule == null ? void 0 : appModule.features.find(feature2 => feature2[dataKey] === featureCode)) == null ? void 0 : _a[dataKey]) || featureCode;
};
export { usePermission as default, isOwnRecord, usePermission };